import { User } from './user';

/**
 * Raktár entitás típusdefiníciója
 */
export interface Warehouse {
  id: string;
  organizationId?: string; // multi-tenant: melyik szervezeté
  name: string;
  code: string;
  location?: string;
  description?: string;
  isActive: boolean;
  isDefault?: boolean;
  
  // Értékesítési funkciók
  allowSales?: boolean; // Engedélyezi-e az értékesítést ebből a raktárból
  salesChannelType?: 'STORE' | 'WEBSHOP' | 'WHOLESALE' | 'MIXED'; // Milyen típusú értékesítés
  allowOnlineOrders?: boolean; // Online rendelések fogadása
  requiresStockReservation?: boolean; // Készletfoglalás szükséges-e
  defaultTaxRate?: number; // Alapértelmezett áfa kulcs
  currency?: string; // Alapértelmezett valutanem
  salesContactEmail?: string; // Értékesítési kapcsolattartó email
  salesContactPhone?: string; // Értékesítési kapcsolattartó telefon
  
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

/**
 * Raktárhely típusdefiníciója (raktáron belüli lokáció)
 */
export interface WarehouseLocation {
  id: string;
  organizationId?: string;
  warehouseId: string;
  name: string;
  code: string;
  description?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Készlet típusdefiníciója
 */
export interface Inventory {
  id: string;
  organizationId?: string;
  productId: string;
  productCode: string;
  productName: string;
  warehouseId: string;
  warehouseName: string;
  locationId?: string;
  locationName?: string;
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  optimalQuantity?: number;
  reservedQuantity?: number;
  unit: string;
  lastUpdated: Date;
  updatedBy?: string;
}

/**
 * Készletmozgás típusai
 */
export enum InventoryMovementType {
  STOCK_IN = 'STOCK_IN', // Bevételezés
  STOCK_OUT = 'STOCK_OUT', // Kiadás
  STOCK_TRANSFER = 'STOCK_TRANSFER', // Raktárak közötti átmozgatás
  STOCK_ADJUSTMENT = 'STOCK_ADJUSTMENT', // Készletkorrekció
  STOCK_INVENTORY = 'STOCK_INVENTORY', // Leltár
  STOCK_RESERVATION = 'STOCK_RESERVATION', // Készletfoglalás
  STOCK_RESERVATION_RELEASE = 'STOCK_RESERVATION_RELEASE', // Készletfoglalás feloldása
}

/**
 * Készletmozgás típusdefiníciója
 */
export interface InventoryMovement {
  id: string;
  organizationId?: string;
  productId: string;
  productCode: string;
  productName: string;
  warehouseId: string;
  warehouseName: string;
  targetWarehouseId?: string; // Csak átmozgatás esetén
  targetWarehouseName?: string; // Csak átmozgatás esetén
  locationId?: string;
  locationName?: string;
  quantity: number;
  unit: string;
  date: Date;
  type: InventoryMovementType;
  documentId?: string; // Kapcsolódó dokumentum (számla, szállítólevél, stb.)
  documentType?: string; // Dokumentum típusa
  documentNumber?: string; // Dokumentum száma
  userId: string;
  userName: string;
  userEmail?: string;
  note?: string;
  lotNumber?: string; // Gyártási szám
  expiryDate?: Date; // Lejárati dátum
  serialNumber?: string; // Sorozatszám
  attachments?: string[]; // URLs of uploaded documents (invoices, delivery notes, etc.)
  targetLocationId?: string;
  /** kiadás / korrekció oka: sale | worksheet | internal | scrap | damaged | lost | correction | other */
  reason?: string;
  customerId?: string;
  worksheetId?: string;
  stockCountId?: string;  // leltár azonosító
  unitCost?: number;      // beszerzési egységár a mozgás pillanatában (készletérték)
  quantityBefore?: number;
  quantityAfter?: number;
  delta?: number;         // tényleges készletváltozás (korrekciónál / leltárnál is)
  isActive: boolean;
  createdAt: Date;
}

/**
 * Készletmozgás létrehozásához használt adatstruktúra
 */
export interface CreateInventoryMovementData {
  productId: string;
  warehouseId: string;
  targetWarehouseId?: string;
  locationId?: string;
  quantity: number;
  type: InventoryMovementType;
  documentId?: string;
  documentType?: string;
  documentNumber?: string;
  note?: string;
  lotNumber?: string;
  expiryDate?: Date;
  serialNumber?: string;
  attachments?: string[]; // URLs of uploaded documents (invoices, delivery notes, etc.)
  targetLocationId?: string;
  reason?: string;
  customerId?: string;
  worksheetId?: string;
  stockCountId?: string;
  unitCost?: number;
  date?: Date;
}

/**
 * Raktár létrehozásához használt adatstruktúra
 */
export interface CreateWarehouseData {
  name: string;
  code: string;
  location?: string;
  description?: string;
  isActive?: boolean;
  isDefault?: boolean;
  
  // Értékesítési beállítások
  allowSales?: boolean;
  salesChannelType?: 'STORE' | 'WEBSHOP' | 'WHOLESALE' | 'MIXED';
  allowOnlineOrders?: boolean;
  requiresStockReservation?: boolean;
  defaultTaxRate?: number;
  currency?: string;
  salesContactEmail?: string;
  salesContactPhone?: string;
}

/**
 * Raktárhely létrehozásához használt adatstruktúra
 */
export interface CreateWarehouseLocationData {
  warehouseId: string;
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
}

/**
 * Készletjelentés típusdefiníciója
 */
export interface InventoryReport {
  productId: string;
  productCode: string;
  productName: string;
  totalQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  unit: string;
  warehouseDetails: {
    warehouseId: string;
    warehouseName: string;
    quantity: number;
    reservedQuantity: number;
    availableQuantity: number;
  }[];
}

/**
 * Készletlekérdezési paraméterek
 */
export interface InventoryQueryParams {
  productId?: string;
  productCode?: string;
  warehouseId?: string;
  locationId?: string;
  belowMinimum?: boolean;
  aboveMaximum?: boolean;
  onlyAvailable?: boolean;
  searchTerm?: string;
} 